import { AfterViewInit, Component, OnDestroy, viewChild } from '@angular/core';

import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

import { BaasActionButton, BaasActionButtonOptions } from '@lvflanders/bar-ng-ui';

/** Contract for BaasActionButtonCellRenderer parameters. */
export interface IBaasActionButtonCellRendererParams extends ICellRendererParams {
    /** @experimental When the cell renderer gets the focus make sure the action button is activated (support executing it with enter). */
    activateOnEnter?: boolean;
    actionButton: (rowData: any) => BaasActionButtonOptions;
}

@Component({
    template: '<baas-action-button [item]="actionButton" [zeroWhiteLineIfHidden]="true"></baas-action-button>',
    selector: 'bar-action-button-cell-renderer',
    imports: [BaasActionButton]
})
/** BAR ag-grid cell renderer to render a BAR action button. */
export class BaasActionButtonCellRenderer implements ICellRendererAngularComp, AfterViewInit, OnDestroy {
    readonly actionButtonElement = viewChild.required(BaasActionButton);

    #destroyFns: (() => void)[] = [];

    protected params: IBaasActionButtonCellRendererParams;
    protected actionButton: BaasActionButtonOptions;

    refresh(params: any): boolean {
        return false;
    }

    agInit(params: IBaasActionButtonCellRendererParams): void {
        this.params = params;
        let actionButton = (<any>this.params).actionButton;
        if (actionButton && actionButton instanceof Function) actionButton = actionButton(this.params.data);
        this.actionButton = actionButton;
    }

    ngOnDestroy(): void {
        this.#destroyFns.forEach((fn) => fn());
        this.#destroyFns = [];
    }

    ngAfterViewInit(): void {
        // Set up the cell focus listener
        if (this.params && this.params.api && this.params.activateOnEnter === true) {
            const focusListener = (event: any) => {
                if (event.rowIndex === this.params.node.rowIndex && event.column === this.params.column) {
                    this.actionButtonElement()?.focus();
                }
            };

            this.params.api.addEventListener('cellFocused', focusListener);
            this.#destroyFns.push(() => this.params.api.removeEventListener('cellFocused', focusListener));

            const keyDownListener = (event: any) => {
                // Check if this is our cell and Enter key was pressed
                if (event.rowIndex === this.params.node.rowIndex && (<any>event.event).key === 'Enter') {
                    // Additional check to make sure it's the right column by comparing data or other identifiers
                    const focusedCell = this.params.api.getFocusedCell();
                    if (focusedCell && focusedCell.column === this.params.column && this.actionButtonElement()) {
                        this.actionButtonElement().execute();
                        event.event.stopPropagation();
                        event.event.preventDefault();
                    }
                }
            };

            this.params.api.addEventListener('cellKeyDown', keyDownListener);

            this.#destroyFns.push(() => this.params.api.removeEventListener('cellKeyDown', keyDownListener));
        }
    }
}
